'use client';

import { useEffect, useState } from 'react';
import Link from 'next/link';
import { usePathname } from 'next/navigation';
import { Car, X, ChevronRight } from 'lucide-react';
import { useCartStore } from '@/stores/cart';

/**
 * Slim sticky bar at the top of the viewport when the user has selected a
 * vehicle. Surfaces "you're shopping for {car}" context everywhere except
 * pages that already make it explicit (fordon, produkt, kassa, varukorg).
 *
 * Only appears after a small scroll so it doesn't fight the hero on page 1.
 */
export function StickyVehicleBar() {
  const selectedVehicle = useCartStore((s) => s.selectedVehicle);
  const selectedPlate = useCartStore((s) => s.selectedPlate);
  const clearVehicle = useCartStore((s) => s.clearVehicle);
  const pathname = usePathname();
  const [scrolled, setScrolled] = useState(false);
  const [mounted, setMounted] = useState(false);

  useEffect(() => setMounted(true), []);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 240);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  if (!mounted || !selectedVehicle) return null;

  // Skip on pages that already show the vehicle prominently
  const hidden = [
    '/fordon/',
    '/produkt/',
    '/kassa',
    '/varukorg',
  ].some((p) => pathname?.startsWith(p));
  if (hidden) return null;
  if (!scrolled) return null;

  const modelName = selectedVehicle.model.replace(/\s*\(.*$/, '').trim();

  return (
    <div className="fixed top-0 inset-x-0 z-40 bg-[#0f2e5a] text-white shadow-sm">
      <div className="container mx-auto px-3 sm:px-4 py-2 flex items-center justify-between gap-2">
        <Link
          href={`/fordon/${selectedVehicle.ktype}`}
          className="flex items-center gap-2 min-w-0 flex-1 hover:text-amber-300 transition-colors"
        >
          <Car className="h-4 w-4 shrink-0 text-amber-300" />
          <span className="text-xs sm:text-sm font-medium truncate">
            <span className="hidden sm:inline">Du letar delar för: </span>
            <strong className="font-semibold">
              {selectedVehicle.manufacturer} {modelName}
              {selectedVehicle.cartype ? ` · ${selectedVehicle.cartype}` : ''}
            </strong>
            {selectedPlate && (
              <span className="ml-1.5 font-mono opacity-80 hidden sm:inline">({selectedPlate})</span>
            )}
          </span>
          <ChevronRight className="h-3.5 w-3.5 shrink-0 opacity-70" />
        </Link>
        <button
          type="button"
          onClick={clearVehicle}
          aria-label="Avmarkera bil"
          className="shrink-0 inline-flex items-center justify-center h-7 w-7 rounded-full hover:bg-white/10 transition-colors"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
